import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminListHosts } from '../api/adminHosts';

const statusOptions = [
  { value: '', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'BLOCKED', label: 'Blocked' },
];

const AdminHostsList = ({ token }) => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('');
  const [kycStatus, setKycStatus] = useState('');
  const [payoutStatus, setPayoutStatus] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: 20 });

  const load = async (page = pagination.page || 1) => {
    setLoading(true);
    setError('');
    try {
      const res = await adminListHosts(token, {
        status,
        q,
        page,
        limit: pagination.limit,
        kyc_status: kycStatus,
        payout_status: payoutStatus,
      });
      setItems(res.data || []);
      setPagination(res.pagination || { page, totalPages: 1, total: res.data?.length || 0, limit: pagination.limit });
    } catch (err) {
      setError(err.message || 'Failed to load hosts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, kycStatus, payoutStatus]);

  return (
    <div className="admin-card rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-emerald-700">Hosts / Partners</p>
          <h1 className="admin-page-title text-xl font-bold text-slate-900">Hosts list</h1>
          <p className="text-sm text-slate-600">Search, filter, and review host accounts.</p>
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-5">
        <input
          type="search"
          placeholder="Search name, email, phone"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load(1)}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        >
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          value={kycStatus}
          onChange={(e) => setKycStatus(e.target.value)}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        >
          <option value="">KYC: All</option>
          <option value="APPROVED">KYC Approved</option>
          <option value="PENDING">Pending</option>
          <option value="REJECTED">Rejected</option>
        </select>
        <select
          value={payoutStatus}
          onChange={(e) => setPayoutStatus(e.target.value)}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        >
          <option value="">Payout: All</option>
          <option value="verified">Verified</option>
          <option value="pending">Pending</option>
          <option value="rejected">Rejected</option>
        </select>
        <button
          type="button"
          onClick={() => load(1)}
          className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800"
        >
          Apply
        </button>
      </div>

      {error && <div className="mt-3 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Host</th>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">KYC</th>
              <th className="px-4 py-3">Payout</th>
              <th className="px-4 py-3">Properties</th>
              <th className="px-4 py-3">Pending payout</th>
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-3 text-slate-500">
                  Loading...
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-3 text-slate-500">
                  No hosts found.
                </td>
              </tr>
            )}
            {items.map((h) => (
              <tr key={h.id} className="hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {h.profile_image_url ? (
                      <img
                        src={h.profile_image_url.startsWith('http') ? h.profile_image_url : `${process.env.REACT_APP_API_BASE_URL || ''}${h.profile_image_url}`}
                        alt={h.full_name}
                        className="h-10 w-10 rounded-full border border-slate-200 object-cover"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-sm font-semibold text-slate-700">
                        {(h.full_name || 'H')[0]}
                      </div>
                    )}
                    <div>
                      <div className="font-semibold text-slate-900">{h.full_name || 'Host'}</div>
                      {(h.business_name || h.company_name) && <div className="text-xs text-slate-500">{h.business_name || h.company_name}</div>}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-700">
                  <div>{h.phone}</div>
                  <div className="text-xs text-slate-500">{h.email}</div>
                </td>
                <td className="px-4 py-3 text-xs">
                  <span className="rounded-full bg-slate-100 px-2 py-1 font-semibold text-slate-700">{(h.kyc_status || 'not_submitted').toUpperCase()}</span>
                </td>
                <td className="px-4 py-3 text-xs">
                  <span className="rounded-full bg-slate-100 px-2 py-1 font-semibold text-slate-700">
                    {(h.payout_verification_status || 'pending').toUpperCase()}
                  </span>
                </td>
                <td className="px-4 py-3">{h.properties_count}</td>
                <td className="px-4 py-3">
                  INR {Number(h.pending_payouts || 0).toLocaleString()}
                </td>
                <td className="px-4 py-3">
                  <Link
                    to={`/admin/hosts/${h.id}`}
                    className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    View
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex items-center justify-between text-sm text-slate-600">
        <div>
          Page {pagination.page || 1} of {pagination.totalPages || 1}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => load(Math.max(1, (pagination.page || 1) - 1))}
            className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50"
            disabled={(pagination.page || 1) <= 1}
          >
            Prev
          </button>
          <button
            type="button"
            onClick={() => load((pagination.page || 1) + 1)}
            className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50"
            disabled={(pagination.page || 1) >= (pagination.totalPages || 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminHostsList;

